import React from 'react';
import PropTypes from 'prop-types';
import { Controller, useWatch } from 'react-hook-form';
import {
  FormHelperText,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  Typography,
  Box,
} from '@mui/material';
import HelpTooltip from '../../../tooltips/help-tooltip/HelpTooltip';
import getAriaDescribedBy from '../../helpers/ariaDescribedBy';

const LegendHeading = (
  title,
  titleHeading,
  titleHeadingClassName,
  titleHeadingStyle,
  additionalHeading,
) => {
  if (!title) {
    return null;
  }
  let HeadingTag;
  switch (titleHeading) {
    case 'h2':
      HeadingTag = 'h2';
      break;
    case 'h3':
      HeadingTag = 'h3';
      break;
    case 'h4':
      HeadingTag = 'h4';
      break;
    case 'h5':
      HeadingTag = 'h5';
      break;
    case 'h6':
      HeadingTag = 'h6';
      break;
    case 'p':
      HeadingTag = 'p';
      break;
    case 'span':
      HeadingTag = 'span';
      break;
    default:
      HeadingTag = 'h2';
      break;
  }

  return (
    <HeadingTag className={titleHeadingClassName} style={titleHeadingStyle}>
      {title} <span style={{ color: 'var(--quick-palette-almost-black-l-30-dark-gray, #5E7079)', fontWeight: 400 }}>{additionalHeading}</span>
    </HeadingTag>
  );
};

const ConfirmRadio = ({
  ariaDescribedBy,
  ariaLabelledBy,
  id,
  name,
  title,
  titleHeading,
  titleHeadingClassName,
  titleHeadingStyle,
  tooltipText,
  tooltipSpacing,
  description,
  descriptionNote,
  control,
  className,
  trueLabel,
  falseLabel,
  onChangeCallback,
  trueEnabled,
  falseEnabled,
  hideDisabled,
  disabled,
  ariaLabel,
  hideRadio,
  children,
  additionalHeading,
  ariaRequired,
}) => {
  const formControlName = useWatch({
    name,
    control,
  });

  return (
    <fieldset id={`${id}-radio-group`} className={`radio-group-fieldset ${hideRadio ? 'hideRadio' : ''}`}>
      {children && (
        <legend>{children}</legend>
      )}
      {!children && title && (
        <legend id={`${id}-legend`}>
          <span className="headerWithTooltip">
            {LegendHeading(
              title,
              titleHeading,
              titleHeadingClassName,
              titleHeadingStyle,
              additionalHeading,
            )}
            {tooltipText && (
              <span className="headerTooltip-btn" style={{ marginLeft: tooltipSpacing }}>
                <HelpTooltip
                  id={`${id}-tooltip`}
                  placement="top"
                  iconSize="inherit"
                  title={tooltipText}
                />
              </span>
            )}
          </span>
        </legend>
      )}
      {!children && description && (
        <Typography
          className={descriptionNote ? 'note desc-radio-group' : 'desc-radio-group'}
        >
          {description}
        </Typography>
      )}
      <Controller
        name={name}
        control={control}
        defaultValue=""
        render={({ field, fieldState: { invalid, error } }) => (
          <FormControl variant="standard" className="radio-group" data-error={invalid}>
            <RadioGroup
              {...field}
              id={`${id}-input`}
              aria-labelledby={ariaLabelledBy || `${id}-legend`}
              aria-describedby={getAriaDescribedBy(
                ariaDescribedBy,
                `${id}-helper-text`,
                !!error?.message,
              ) || undefined}
              aria-label={ariaLabel}
              row
              onChange={event => {
                field.onChange(event);
                // This form uses onBlur validation; trigger blur on selection
                // so stale errors clear immediately once user picks a value.
                field.onBlur();
                onChangeCallback(event.target.value);
              }}
              sx={{
                ...className,
                ...(hideRadio && {
                  display: 'none',
                }),
              }}
              data-radio-value={formControlName}
              aria-required={ariaRequired}
            >
              {(trueEnabled || !hideDisabled) && (
                <FormControlLabel
                  value="True"
                  control={(
                    <Radio
                      id={`${id}-${trueLabel.replace(' ', '').toLowerCase()}`}
                      color="default"
                    />
                  )}
                  label={trueLabel}
                  className={ disabled && 'disabledRadio' }
                  sx={{ textDecoration: trueEnabled ? 'none' : 'line-through' }}
                  disabled={!trueEnabled || disabled}
                  data-radio="Yes"
                />
              )}
              {(falseEnabled || !hideDisabled) && (
                <FormControlLabel
                  value="False"
                  control={(
                    <Radio
                      id={`${id}-${falseLabel.replace(' ', '').toLowerCase()}`}
                      color="default"
                    />
                  )}
                  label={falseLabel}
                  className={ disabled && 'disabledRadio' }
                  sx={{ textDecoration: falseEnabled ? 'none' : 'line-through' }}
                  disabled={!falseEnabled || disabled}
                  data-radio="No"
                />
              )}
            </RadioGroup>
            {invalid && (
              <FormHelperText id={`${id}-helper-text`} error={invalid}>
                {error?.message}
              </FormHelperText>
            )}
          </FormControl>
        )}
      />
    </fieldset>
  );
};

ConfirmRadio.propTypes = {
  tooltipSpacing: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  titleHeading: PropTypes.string,
  titleHeadingClassName: PropTypes.string,
  titleHeadingStyle: PropTypes.shape({}),
  tooltipText: PropTypes.string,
  description: PropTypes.string,
  descriptionNote: PropTypes.bool,
  control: PropTypes.shape({}).isRequired,
  required: PropTypes.bool,
  className: PropTypes.string,
  trueLabel: PropTypes.string,
  falseLabel: PropTypes.string,
  trueEnabled: PropTypes.bool,
  falseEnabled: PropTypes.bool,
  hideDisabled: PropTypes.bool,
  onChangeCallback: PropTypes.func,
  ariaLabel: PropTypes.string,
  children: PropTypes.element,
  hideRadio: PropTypes.bool,
  disabled: PropTypes.bool,
  additionalHeading: PropTypes.string,
  ariaRequired: PropTypes.bool,
};

ConfirmRadio.defaultProps = {
  tooltipSpacing: undefined,
  title: '',
  titleHeading: 'h2',
  titleHeadingClassName: '',
  titleHeadingStyle: {},
  tooltipText: '',
  description: '',
  descriptionNote: false,
  ariaDescribedBy: '',
  ariaLabelledBy: '',
  required: false,
  className: '',
  trueLabel: 'Yes',
  falseLabel: 'No',
  trueEnabled: true,
  falseEnabled: true,
  hideDisabled: true,
  onChangeCallback: () => { },
  ariaLabel: '',
  children: null,
  hideRadio: false,
  disabled: false,
  additionalHeading: '',
  ariaRequired: false,
};

export default ConfirmRadio;
